import type {
  ConnectionDeleteResult,
  ConnectionListResult,
  ConnectionRepository,
  ConnectionVerifyResult,
  NormalizedConnection,
  PlatformUrn,
} from '@mint-lib/integration-registry';
import { useQuery } from '@mint-lib/query-client';
import type { QueryClient } from '@mint-lib/query-client';
import {
  OmniTraffickingIntegrationsTypes,
  PlatformUrns,
} from '@mint-sdk/api-integrations';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

/** AdForm is a fixed `platform_v2` URN — this repository only ever serves that one integration. */
export const ADFORM_URN: PlatformUrn =
  PlatformUrns.TRAFFICKING_PLATFORM_URN.adform;

/** Mirrors the legacy `AxiosError` shape read in `useVerifyConnection`/`useGetConnectionList`. */
interface AdFormApiError {
  response?: {
    data?: {
      error?: {
        type?: string;
        message?: string;
      };
    };
  };
}

/** Maps an AdServer list item (AdForm's branch of `OmniTraffickingIntegration`) into the shared shape. */
export function toNormalizedAdFormConnection(
  item: OmniTraffickingIntegrationsTypes.OmniTraffickingIntegration,
): NormalizedConnection {
  return {
    id: item.id,
    name: item.name,
    platformUrn: ADFORM_URN,
    // The list endpoint doesn't return verification state — `useVerify` fetches it separately.
    isVerified: null,
    isExpired: Boolean(item.expired),
    usingInAccounts: Boolean(item.usingInAccounts),
    userId: item.userId ?? null,
    extra: { adformAdvertiser: item.extra.adformAdvertiser },
  };
}

function resolveAdServerUrn(
  platform: string,
): OmniTraffickingIntegrationsTypes.AdserverUrnType {
  const parsed =
    OmniTraffickingIntegrationsTypes.AdserverUrnEnum.safeParse(platform);
  return parsed.success
    ? parsed.data
    : OmniTraffickingIntegrationsTypes.TRAFFICKING_PLATFORM_URN.adform;
}

function useList(params: { platform: string }): ConnectionListResult {
  const query = useQuery({
    ...Integrations.getIntegrationList({
      params: { adServer: resolveAdServerUrn(params.platform) },
    }),
  });

  return {
    connections: (query.data ?? []).map(toNormalizedAdFormConnection),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

function useVerify(params: {
  platform: string;
  connectionId: string;
}): ConnectionVerifyResult {
  const query = useQuery({
    ...Integrations.getIntegrationIsVerified({
      params: {},
      urlParams: { integrationId: params.connectionId },
    }),
    enabled: Boolean(params.connectionId),
  });

  const apiError = query.error as AdFormApiError | null;

  return {
    isVerified: query.data,
    isLoading: query.isLoading,
    isWrongCustomerError:
      apiError?.response?.data?.error?.type === 'WrongCustomerError',
    refetch: query.refetch,
  };
}

function useDelete(): ConnectionDeleteResult {
  const { mutate, isPending } = Integrations.deleteIntegration();

  return {
    remove: (id, { onSuccess, onError }) => {
      mutate({ urlParams: { integrationId: id } }, { onSuccess, onError });
    },
    isPending,
  };
}

/** `ConnectionRepository` implementation for AdForm — the AdServer branch of the legacy dispatch hooks. */
export const adFormConnectionRepository: ConnectionRepository = {
  useList,
  useVerify,
  useDelete,
};

/**
 * Loader-safe (non-hook) equivalent of `useList`, for `definition.ts`'s
 * `prefetch`. Router loaders run outside React, so they can't call the
 * `useList` hook — they fetch the same query directly through the
 * `QueryClient` instead.
 */
export async function fetchAdFormConnections(
  queryClient: QueryClient,
): Promise<NormalizedConnection[]> {
  const items = await queryClient.ensureQueryData(
    Integrations.getIntegrationList({
      params: {
        adServer:
          OmniTraffickingIntegrationsTypes.TRAFFICKING_PLATFORM_URN.adform,
      },
    }),
  );

  return (items ?? []).map(toNormalizedAdFormConnection);
}
